<template>
  <div class="tt-page">
    <div class="tt-page-content flex">
      <div class="tt-page-content-left flex-shrink">
        <div ref="mapEl" class="tt-page-content-left-map" />
      </div>
      <div class="tt-page-content-right flex-1">
        <div class="tt-page-content-right-top flex-row-center">
          <div class="icon flex-center" @click="toTop">
            <img src="@/assets/img/icon-map-top.png">
          </div>
          <div class="icon flex-center" @click="mapPre">
            <img src="@/assets/img/icon-map-pre.png">
          </div>
          <div class="icon flex-center" @click="playTrack">
            <img src="@/assets/img/icon-map-start.png">
          </div>
          <div class="icon flex-center" @click="next">
            <img src="@/assets/img/icon-map-next.png">
          </div>
          <div class="icon flex-center" @click="toBottom">
            <img src="@/assets/img/icon-map-end.png">
          </div>
        </div>
        <div v-if="paths" class="tt-page-content-right-list ">
          <div class="position-relative">
            <div class="tt-page-content-right-list-percent position-absolute">
              <span class="line position-absolute" :style="{height: percent + '%'}" />
            </div>
            <div v-for="(item, index) in paths" :key="index" class="tt-page-content-right-list-item flex-row-center cursor" @click="changeMap(index)">
              <span class="num flex-shrink">
                <p class="flex-center" :class="curClass(index)">{{ index + 1 }}</p>
              </span>
              <span class="text flex-1">
                <p class="tit text-line-1 font-14">{{ item.name }}</p>
                <p class="date font-12">{{ item.startDate }}<span>/</span>{{ item.coordinate }}</p>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { TIANDITU_TK } from '@/utils/tianditu.js'

/** 天地图 WMTS（Web 墨卡托），国内可访问 */
function createTiandituLayers(tk) {
  const subdomains = ['0', '1', '2', '3', '4', '5', '6', '7']
  const vecUrl = `https://t{s}.tianditu.gov.cn/vec_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=vec&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`
  const cvaUrl = `https://t{s}.tianditu.gov.cn/cva_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cva&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${tk}`
  const common = {
    subdomains,
    maxZoom: 18,
    tileSize: 256,
    zoomOffset: 0
  }
  return {
    vec: L.tileLayer(vecUrl, {
      ...common,
      attribution: '&copy; <a href="https://www.tianditu.gov.cn/">天地图</a>'
    }),
    cva: L.tileLayer(cvaUrl, common)
  }
}

export default {
  props: {
    tracks: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      paths: [],
      percent: 0,
      map: null,
      markers: [],
      dynamicPolyline: null,
      animateMarker: null,
      isPlaying: false,
      activeIndex: -1,
      animateTimer: null,
      defaultZoom: 14,
      playZoom: 4,
      focusZoom: 9,
      currentStep: 0,
      currentFrame: 0,
      animateSpeed: 20,
      dynamicPath: [],
      baseStepSpeed: 0.8
    }
  },
  mounted() {
    this.handleData()
  },
  beforeDestroy() {
    this.pauseTrack()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    /** mapPlace 存 [lng, lat]，Leaflet 使用 [lat, lng] */
    toLatLng(place) {
      return L.latLng(place[1], place[0])
    },

    handleData() {
      if (this.tracks && this.tracks.length) {
        const ary = this.tracks.map(v => v.property).filter(v => v.mapPlace)
        ary.forEach(v => {
          const res = String(v.mapPlace).split(',')
          v.mapPlace = [parseFloat(res[0]), parseFloat(res[1])]
        })
        this.paths = ary
        this.$nextTick(() => {
          this.createMapInstance()
        })
      }
    },

    createMapInstance() {
      if (!this.$refs.mapEl || !this.paths.length) return

      const center = this.toLatLng(this.paths[0].mapPlace)
      this.map = L.map(this.$refs.mapEl, {
        zoomControl: false,
        attributionControl: true,
        minZoom: 1,
        maxZoom: 18
      }).setView(center, this.defaultZoom)

      // 天地图矢量底图 + 注记
      const { vec, cva } = createTiandituLayers(TIANDITU_TK)
      vec.addTo(this.map)
      cva.addTo(this.map)

      // Leaflet 在容器初次渲染时需强制刷新尺寸
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize()
        }
      })

      this.initMarkers()
      this.changeMap(0)
    },

    createLocationIcon(active) {
      const color = active ? '#A21E1E' : '#666666'
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
        <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M14 1C7.4 1 2 6.4 2 13c0 8.4 10.2 20.2 11.1 21.2a1.2 1.2 0 0 0 1.8 0C15.8 33.2 26 21.4 26 13 26 6.4 20.6 1 14 1z"/>
        <circle cx="14" cy="13" r="4.5" fill="#fff"/>
      </svg>`
      return L.divIcon({
        className: 'tt-leaflet-marker',
        html: svg,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -34]
      })
    },

    createAnimateIcon() {
      return L.divIcon({
        className: 'tt-leaflet-animate-marker',
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="26" viewBox="0 0 28 36">
          <path fill="#A21E1E" stroke="#FFF7AA" stroke-width="2" d="M14 1C7.4 1 2 6.4 2 13c0 8.4 10.2 20.2 11.1 21.2a1.2 1.2 0 0 0 1.8 0C15.8 33.2 26 21.4 26 13 26 6.4 20.6 1 14 1z"/>
          <circle cx="14" cy="13" r="4.5" fill="#fff"/>
        </svg>`,
        iconSize: [20, 26],
        iconAnchor: [10, 26]
      })
    },

    initMarkers() {
      this.markers = []
      this.paths.forEach((item, index) => {
        const marker = L.marker(this.toLatLng(item.mapPlace), {
          icon: this.createLocationIcon(false),
          title: item.name
        })
        marker.on('click', () => {
          this.handleMarkerClick({
            index,
            data: { ...item }
          })
        })
        this.markers.push(marker)
      })
    },

    showMarker(index) {
      const marker = this.markers[index]
      if (!marker || !this.map) return
      marker.setIcon(this.createLocationIcon(true))
      if (!this.map.hasLayer(marker)) {
        marker.addTo(this.map)
      }
    },

    hideAllMarkers() {
      this.markers.forEach(marker => {
        if (this.map && this.map.hasLayer(marker)) {
          this.map.removeLayer(marker)
        }
      })
    },

    clearDynamicTrack() {
      if (this.dynamicPolyline && this.map && this.map.hasLayer(this.dynamicPolyline)) {
        this.map.removeLayer(this.dynamicPolyline)
      }
      this.dynamicPolyline = null
      this.dynamicPath = []

      if (this.animateMarker && this.map && this.map.hasLayer(this.animateMarker)) {
        this.map.removeLayer(this.animateMarker)
      }
      this.animateMarker = null
    },

    /**
     * 播放轨迹动画
     */
    playTrack() {
      if (this.isPlaying || !this.map) {
        this.pauseTrack()
        return
      }
      if (this.paths.length < 2) return

      this.isPlaying = true
      this.currentStep = 0
      this.currentFrame = 0
      this.activeIndex = 0
      this.percent = 0

      this.hideAllMarkers()
      this.clearDynamicTrack()

      const startLatLng = this.toLatLng(this.paths[0].mapPlace)
      this.map.setView(startLatLng, this.playZoom, { animate: false })

      this.dynamicPath = [startLatLng]
      this.dynamicPolyline = L.polyline(this.dynamicPath, {
        color: '#A21E1E',
        weight: 3,
        opacity: 0.85
      }).addTo(this.map)

      this.animateMarker = L.marker(startLatLng, {
        icon: this.createAnimateIcon(),
        zIndexOffset: 1000
      }).addTo(this.map)

      this.startAnimate()
    },

    /**
     * 轨迹动画：按段插值，镜头跟随，折线逐步延伸
     */
    startAnimate() {
      if (!this.isPlaying || !this.map) {
        this.isPlaying = false
        return
      }

      if (this.currentStep >= this.paths.length - 1) {
        this.isPlaying = false
        this.showMarker(this.paths.length - 1)
        return
      }

      const startPoint = this.paths[this.currentStep].mapPlace
      const endPoint = this.paths[this.currentStep + 1].mapPlace

      const stepCount = this.getDynamicStepCount(
        startPoint[1],
        startPoint[0],
        endPoint[1],
        endPoint[0]
      )
      const stepLat = (endPoint[1] - startPoint[1]) / stepCount
      const stepLng = (endPoint[0] - startPoint[0]) / stepCount

      this.animateTimer = setInterval(() => {
        if (!this.isPlaying) {
          clearInterval(this.animateTimer)
          this.animateTimer = null
          return
        }

        this.currentFrame++
        const currentLat = startPoint[1] + stepLat * this.currentFrame
        const currentLng = startPoint[0] + stepLng * this.currentFrame
        const currentPos = L.latLng(currentLat, currentLng)

        if (this.animateMarker) {
          this.animateMarker.setLatLng(currentPos)
        }

        this.map.panTo(currentPos, { animate: false })

        this.dynamicPath.push(currentPos)
        if (this.dynamicPolyline) {
          this.dynamicPolyline.setLatLngs(this.dynamicPath)
        }

        if (this.currentFrame >= stepCount) {
          clearInterval(this.animateTimer)
          this.animateTimer = null
          this.currentStep++
          this.activeIndex = this.currentStep
          this.percent = parseFloat(this.activeIndex) / parseFloat(this.paths.length - 1) * 100
          this.currentFrame = 0

          // 到达节点时短暂显示标记
          this.showMarker(this.currentStep)

          this.map.panTo(this.toLatLng(endPoint), { animate: false })
          this.startAnimate()
        }
      }, this.animateSpeed)
    },

    calculateDistance(lat1, lng1, lat2, lng2) {
      const R = 6371000
      const radLat1 = Math.PI * lat1 / 180
      const radLat2 = Math.PI * lat2 / 180
      const deltaLat = Math.PI * (lat2 - lat1) / 180
      const deltaLng = Math.PI * (lng2 - lng1) / 180
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(radLat1) * Math.cos(radLat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },

    getDynamicStepCount(startLat, startLng, endLat, endLng) {
      if (!this.map) return 50

      const distance = this.calculateDistance(startLat, startLng, endLat, endLng)
      let pixelDistance = 0
      try {
        const startPx = this.map.latLngToContainerPoint(L.latLng(startLat, startLng))
        const endPx = this.map.latLngToContainerPoint(L.latLng(endLat, endLng))
        pixelDistance = Math.sqrt(
          Math.pow(endPx.x - startPx.x, 2) +
          Math.pow(endPx.y - startPx.y, 2)
        )
      } catch (e) {
        pixelDistance = Math.max(distance / 1000, 10)
      }

      return Math.max(Math.round(pixelDistance / this.baseStepSpeed), 4)
    },

    handleMarkerClick(v) {
      const page = this.$router.resolve({
        path: '/entity',
        query: {
          cateId: '013003',
          sourceId: v.data.id
        }
      })
      window.open(page.href, '_blank')
    },

    next() {
      if (this.activeIndex === this.paths.length - 1) return
      this.changeMap(this.activeIndex + 1)
    },
    mapPre() {
      if (this.activeIndex < 1) return
      this.changeMap(this.activeIndex - 1)
    },
    toTop() {
      if (this.activeIndex === 0) return
      this.changeMap(0)
    },
    toBottom() {
      if (this.activeIndex === this.paths.length - 1) return
      this.changeMap(this.paths.length - 1)
    },

    changeMap(index) {
      this.pauseTrack()
      if (!this.map || index < 0 || index >= this.paths.length) return
      if (this.activeIndex === index) return

      const isFirst = this.activeIndex < 0
      this.activeIndex = index
      this.percent = this.paths.length > 1
        ? parseFloat(this.activeIndex) / parseFloat(this.paths.length - 1) * 100
        : 0

      this.clearDynamicTrack()
      this.hideAllMarkers()
      this.showMarker(index)

      const target = this.toLatLng(this.paths[index].mapPlace)
      if (isFirst) {
        this.map.setView(target, this.focusZoom, { animate: false })
      } else {
        // 仅平移，不改变缩放（避免缩小再放大）
        if (this.map.getZoom() !== this.focusZoom) {
          this.map.setZoom(this.focusZoom, { animate: false })
        }
        this.map.panTo(target, {
          animate: true,
          duration: 0.8,
          easeLinearity: 0.25
        })
      }
    },

    pauseTrack() {
      this.isPlaying = false
      if (this.animateTimer) {
        clearInterval(this.animateTimer)
        this.animateTimer = null
      }
    },

    curClass(val) {
      if (val < this.activeIndex) {
        return 'num-pass'
      } else if (val === this.activeIndex) {
        return 'num-cursel'
      }
      return ''
    }
  }
}
</script>

<style scoped>
  .tt-page-content {
    width: 100%;
    height: 500px;
    border: 1px solid #EEEEEE;
    border-radius: 5px;
    overflow: hidden;
  }

  .tt-page-content-left-map {
    width: 720px;
    height: 500px;
  }

  .tt-page-content-right {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(100px);
    height: 500px;
  }

  .tt-page-content-right-top {
    height: 40px;
    padding: 0 20px;
    border: 1px solid #EEEEEE;
  }

  .tt-page-content-right-top .icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    margin-right: 5px;
    border: 1px solid #FAFAFA;
    cursor: pointer;
  }

  .tt-page-content-right-list {
    height: 760px;
    overflow: scroll;
  }

  .tt-page-content-right-list-percent {
    top: 20px;
    left: 26px;
    bottom: 36px;
    background-color: #999;
    width: 2px;
    z-index: -1;
  }

  .tt-page-content-right-list-percent .line {
    background-color: #A21E1E;
    top: 0;
    left: 0;
    width: 2px;
    height: 0.5%;
    transition: 2s;
  }

  .tt-page-content-right-list-item {
    height: 65px;
    padding: 0 20px;
  }

  .tt-page-content-right-list-item .num {
    margin-right: 10px;
  }

  .tt-page-content-right-list-item .num p {
    width: 14px;
    height: 14px;
    border-radius: 7px;
    background: #999999;
    color: #fff;
    font-size: 8px;
    margin-top: -14px;
    transition: .2s;
  }

  .tt-page-content-right-list-item .num p.num-pass{
    border: 1px solid #A21E1E;
    background-color: #fff;
    color: #A21E1E;
  }

  .tt-page-content-right-list-item .num p.num-cursel{
    background: #A21E1E;
  }

  .tt-page-content-right-list-item .text .tit {
    line-height: 20px;
    color: #1A1A1A;
  }

  .tt-page-content-right-list-item .text .date {
    color: #999999;
    line-height: 20px;
  }

  .tt-page-content-right-list-item .text .date span {
    color: #EEEEEE;
    margin: 0 5px;
  }
</style>

<style>
  /* Leaflet DivIcon 默认白底需去掉，否则编号点显示异常 */
  .tt-leaflet-marker,
  .tt-leaflet-animate-marker {
    background: transparent !important;
    border: none !important;
  }
</style>
